import React, { FC, useMemo, useState } from 'react';

import PropTypes from 'prop-types';

import { AlertWarningFilledIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import ScheduleDropdown from './ScheduleDropdown';

const daysInMonth = () => {
  return Array.from({ length: 31 }, (_, dayIndex) => {
    const day = dayIndex + 1;

    return {
      label: day.toLocaleString(undefined, { minimumIntegerDigits: 2 }),
      value: day,
    };
  });
};

type MonthlyDropdownProps = Omit<
  StylableComponent<HTMLDivElement>,
  'onChange'
> & {
  initialValue: number;
  onChange: (val: number) => void;
};

const MonthlyDropdown: FC<MonthlyDropdownProps> = ({
  className,
  initialValue,
  onChange,
  style,
}) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const [showWarning, setShowWarning] = useState(initialValue >= 29);
  const handleDayChange = (newDay: { value: number }) => {
    setShowWarning(newDay.value >= 29);
    onChange(newDay.value);
  };

  const items = useMemo(() => daysInMonth(), []);

  const initialSelectedItem = useMemo(
    () => items.find((item) => item.value === initialValue),
    [initialValue, items],
  );

  return (
    <>
      <ScheduleDropdown
        style={style}
        className={className}
        items={items}
        onChange={handleDayChange}
        initialSelectedItem={initialSelectedItem}
      />
      {showWarning && (
        <div
          css={[
            {
              display: 'flex',
              marginTop: '10px',
            },
          ]}
        >
          <AlertWarningFilledIcon
            size={16}
            style={{
              color: theme.tokens.colors.icon.warning.value,
              marginRight: '5px',
            }}
          />
          <div
            css={[
              {
                ...theme.typography.bodySmall,
                color: theme.tokens.colors.text.secondary.value,
              },
            ]}
          >
            {T.translate('MONTHLY_WARNING')}
          </div>
        </div>
      )}
    </>
  );
};

MonthlyDropdown.propTypes = {
  /**
   * Class name for the component
   */
  className: PropTypes.string,
  /**
   * Initial selected value for the Dropdown component
   */
  initialValue: PropTypes.number.isRequired,
  /**
   * A callback function called when the selected value has changed
   */
  onChange: PropTypes.func.isRequired,
  /**
   * style for the component
   */
  style: stylePropType,
};

export default MonthlyDropdown;
